import { FC, useState } from 'react';
import { FaChalkboardTeacher, FaChartLine, FaFolder, FaPaperPlane } from 'react-icons/fa';
import { ButtonIconLink } from '../../components/links/ButtonIconLink';
import strings from './home.translations';
import { useCustomer } from '../../services/graph/hooks/useCustomer';
import { Customer } from '../../services/graph/hooks/sidecar.types';
import { ModalWithHeader } from '../../components/modal/ModalWithHeader';
import { Button } from '../../components/button/Button';
import { FiCheck, FiCopy } from 'react-icons/fi';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  customer?: Customer;
}

const ContactModal: FC<ContactModalProps> = ({ open, onClose, customer }) => {
  const [clipboardStatus, setClipboardStatus] = useState<'idle' | 'copied'>('idle');

  const copyToClipboard = (text?: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setClipboardStatus('copied');
    setTimeout(() => setClipboardStatus('idle'), 2000);
  };

  return (
    <ModalWithHeader open={open} onClose={onClose} title={'Contact an advisor'}>
      <div className="w-full flex flex-col items-stretch gap-4 leading-tight">
        <p>Get in touch with your dedicated advisor at Alan Boswell Risk Management.</p>

        <div className="grid grid-cols-2 gap-5 w-full">
          <div className="space-y-1">
            <label className="font-semibold text-primary-800">Name</label>
            <p>{customer?.contact.name}</p>
          </div>
          <div className="space-y-1">
            <label className="font-semibold text-primary-800">Phone</label>
            <p>{customer?.contact.phone}</p>
          </div>
          <div className="col-span-2">
            <label className="font-semibold text-primary-800">Email</label>
            <div className="flex gap-2 items-center">
              <p>{customer?.contact.email}</p>
              <button
                onClick={() => copyToClipboard(customer?.contact.email)}
                className="hover:bg-gray-100 shadow-none justify-center text-primary-std hover:text-gray-900 relative flex items-center gap-2 overflow-clip rounded-md px-3 py-2 text-sm shadow-sm"
              >
                <FiCopy />
                {clipboardStatus === 'copied' && (
                  <span className="bg-gray-100 text-primary-std absolute inset-0 flex items-center justify-center gap-1 text-sm font-medium">
                    <FiCheck />
                  </span>
                )}
              </button>
            </div>
          </div>
          <a className="col-span-2" href={`mailto:${customer?.contact.email}`}>
            <Button primary className="w-full">
              Send Email
            </Button>
          </a>
        </div>
      </div>
    </ModalWithHeader>
  );
};

export const HomeQuickLinks: FC = () => {
  const customer = useCustomer();
  const [contactOpen, setContactOpen] = useState<boolean>(false);

  return (
    <>
      <div className="flex flex-col md:flex-row gap-5">
        <ButtonIconLink
          className="flex-1"
          secondary
          link={{
            title: strings.links.consultancy.title,
            to: 'https://www.alanboswell.com/business/risk-management/health-safety/',
            Icon: FaChartLine,
            external: true
          }}
        />
        <ButtonIconLink
          className="flex-1"
          secondary
          link={{
            title: strings.links.training.title,
            to: 'https://www.alanboswell.com/business/risk-management/training/',
            Icon: FaChalkboardTeacher,
            external: true
          }}
        />
        <ButtonIconLink
          className="flex-1"
          link={{
            title: strings.links.myDocuments.title,
            subtitle: strings.links.myDocuments.subtitle,
            to: '/documents/myDocuments',
            Icon: FaFolder
          }}
        />
        {customer.data?.contact && (
          <ButtonIconLink
            className="flex-1"
            link={{
              title: strings.links.askAdvisor.title,
              subtitle: strings.links.askAdvisor.subtitle,
              onClick: () => setContactOpen(true),
              Icon: FaPaperPlane
            }}
          />
        )}
      </div>
      {customer.data?.contact && (
        <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} customer={customer.data} />
      )}
    </>
  );
};
